import React, { useState } from 'react';
import { AlertTriangle, Copy, RefreshCw, Mail, Clock } from 'lucide-react';

interface OnboardingFailurePageProps {
  organizationData?: {
    companyName?: string;
    userEmail?: string;
    discountCode?: string;
    timestamp?: string;
  };
  onRetry?: () => void;
}

export default function OnboardingFailurePage({ organizationData, onRetry }: OnboardingFailurePageProps) {
  const [copied, setCopied] = useState(false);

  const emailTemplate = `Subject: Organization Onboarding Workflow Failed

Hi Daniel,

I encountered an issue during the organization onboarding process. Here are the details:

Organization Name: ${organizationData?.companyName || 'Not provided'}
User Email: ${organizationData?.userEmail || 'Not provided'}
Discount Code: ${organizationData?.discountCode || 'None'}
Timestamp: ${organizationData?.timestamp || new Date().toISOString()}

Issue: The organization creation appeared to complete successfully, but the system failed to redirect me to the new organization homepage after multiple polling attempts.

Please assist with resolving this issue.

Thank you,
${organizationData?.userEmail || 'User'}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailTemplate);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = emailTemplate;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-2xl relative">
        {/* Header */}
        <div className="flex items-center p-6 border-b">
          <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center mr-4">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Organization Setup Delayed</h2>
            <p className="text-sm text-muted-foreground">
              We're still setting up your organization
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Your organization <strong>"{organizationData?.companyName}"</strong> is being created, 
              but it's taking longer than expected. This sometimes happens due to system load.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2 flex items-center">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try These Steps:
              </h3>
              <ol className="text-sm text-blue-800 space-y-2">
                <li>1. <strong>Refresh this page</strong> - Your organization may be ready now</li>
                <li>2. <strong>Wait 5 minutes</strong> and try refreshing again</li>
                <li>3. If still not working, contact support using the email template below</li>
              </ol>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh Page</span>
            </button>
            
            {onRetry && (
              <button
                onClick={onRetry}
                className="flex-1 border border-input bg-background hover:bg-accent hover:text-accent-foreground px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <Clock className="h-4 w-4" />
                <span>Try Again</span>
              </button>
            )}
          </div>

          {/* Email Template Section */}
          <div className="border-t pt-6">
            <h3 className="font-medium mb-3 flex items-center">
              <Mail className="h-4 w-4 mr-2" />
              Need Help? Contact Support
            </h3>
            
            <p className="text-sm text-muted-foreground mb-3">
              If the issue persists, copy this email template and send it to{' '}
              <a 
                href="mailto:daniel@nexusaspyre.com" 
                className="text-primary hover:underline"
              >
                daniel@nexusaspyre.com
              </a>
            </p>

            <div className="bg-gray-50 border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Email Template</span>
                <button
                  onClick={copyToClipboard}
                  className="bg-primary text-primary-foreground px-3 py-1 rounded text-sm hover:bg-primary/90 transition-colors flex items-center space-x-1"
                >
                  <Copy className="h-3 w-3" />
                  <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <div className="bg-white border rounded p-3 text-sm font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                {emailTemplate}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
